import { Add, Edit, ErrorOutline, OpenInNew } from '@mui/icons-material';
import { Alert, Box, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, List, ListItem, MenuItem, Paper, Select, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Button as MuiButton, withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import MaterialReactTable from 'material-react-table';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  GET_VOUCHER_IMPORT_TEMPLATES_FAILURE,
  GET_VOUCHER_IMPORT_TEMPLATE_FAILURE,
  POST_VOUCHER_IMPORT_TEMPLATE_DRYRUN_FAILURE,
  POST_VOUCHER_IMPORT_TEMPLATE_FAILURE,
  POST_VOUCHER_IMPORT_TEMPLATE_SUBMIT_FAILURE,
  PUT_VOUCHER_IMPORT_TEMPLATE_FAILURE,
} from './importVoucherFiles.types';
import { GUIDS } from 'modules/expenses/vouchers/voucherConstants';
import { ImportTemplateEditor } from './importTemplateEditor.component';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { UploadTemplateFile } from './importAuthFiles.components';
import { VOUCHER_IMPORT_ROWS, getDataTypeLabel, getVoucherImportColumnMappings } from './importVoucherFiles.tableDefinitions';
import { currencyFormatter } from 'utilities/currencyFormatter';
import { getAllCurrencies, getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import { getVoucherImportTemplate, getVoucherImportTemplates, postVoucherImportTemplate, putVoucherImportTemplate, submitVoucherImportTemplateFile, uploadVoucherImportTemplateFile } from './importVoucherFiles.actions';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import Spinner from 'modules/common/spinner.component';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

// Voucher import files are gated using the same Finance.Vouchers.Read/Edit permissions as the
// main Vouchers module.

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1.5),
  },
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  mappingWorkspace: {
    minHeight: 420,
  },
});

const VoucherFileTemplateSelector = props => {
  const { isLoading, onTemplateSelected, selectedTemplateId, templates } = props;

  return (
    <div>
      <FormControl sx={{ minWidth: 350 }} size="small">
        <InputLabel id="voucher-file-template-label">
          Select Voucher File Import Template
        </InputLabel>
        <Select
          labelId="voucher-file-template-label"
          id="voucher-file-template-select"
          label="Select Voucher File Import Template"
          disabled={isLoading}
          value={selectedTemplateId}
          onChange={event => onTemplateSelected(event.target.value)}
        >
          <MenuItem value="">Select a template</MenuItem>
          {templates.map(template => (
            <MenuItem key={getTemplateId(template)} value={String(getTemplateId(template))}>
              {getTemplateName(template)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

VoucherFileTemplateSelector.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onTemplateSelected: PropTypes.func.isRequired,
  selectedTemplateId: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const VoucherImportTemplateEditorButton = props => {
  const { disabled, isEditMode, onClick } = props;
  const Icon = isEditMode ? Edit : Add;

  return (
    <MuiButton variant="contained" color="primary" startIcon={<Icon />} onClick={onClick} disabled={disabled}>
      {isEditMode ? 'Edit Template' : 'Create Import Template'}
    </MuiButton>
  );
};

VoucherImportTemplateEditorButton.defaultProps = {
  disabled: false,
};

VoucherImportTemplateEditorButton.propTypes = {
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const getTemplateId = template => template?.templateId || template?.id || template?.importTemplateId;

const getTemplateName = template => template?.name ?? template?.templateName ?? '';

const getTemplateMappings = template => template?.mappings ?? template?.voucherMappings ?? [];

const getTemplateList = response => {
  if (Array.isArray(response)) return response;

  return response?.templates ?? response?.voucherTemplates ?? response?.data ?? [];
};

const commonEnumerationStoreKeys = ['payeeTypes', 'notPayables', 'voucherTypes', 'batchTypes'];
const mappingTableColumnWidth = `${100 / 5}%`;
const VOUCHER_IMPORT_TEMPLATE_TABS = {
  DRY_RUN_RESULTS: 'dryRunResults',
  IMPORT_RESULTS: 'importResults',
  MAPPING_DEFINITIONS: 'mappingDefinitions',
};

const getEnumerationTitlesFromValues = values => values?.map(value => value.title) ?? [];

const getCurrencyCodes = currencies => currencies?.map(currency => currency.code) ?? [];

const getEnumerationTitles = enumerations => {
  const titlesByStoreKey = commonEnumerationStoreKeys.reduce((acc, storeKey) => ({
    ...acc,
    [storeKey]: getEnumerationTitlesFromValues(enumerations?.[storeKey]),
  }), {});

  return {
    ...titlesByStoreKey,
    currencies: getCurrencyCodes(enumerations?.currencies),
  };
};

const mappingTableProps = {
  muiTableProps: {
    sx: {
      tableLayout: 'fixed',
      width: '100%',
    },
  },
  muiTableHeadCellProps: {
    sx: {
      width: mappingTableColumnWidth,
    },
  },
  muiTableBodyCellProps: {
    sx: {
      maxWidth: 0,
      width: mappingTableColumnWidth,
      '& .MuiFormControl-root': {
        minWidth: 0,
      },
      '& .MuiInputBase-root': {
        minWidth: 0,
      },
      '& .MuiSelect-select': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  },
  enableEditing: false,
  enableRowActions: false,
  enablePagination: false,
  enableBottomToolbar: false,
  enableTopToolbar: false,
  enableColumnActions: false,
  enableColumnFilters: false,
  enableGlobalFilter: false,
  enableDensityToggle: false,
  enableFullScreenToggle: false,
  enableHiding: false,
};

const ImportMappingTable = props => {
  const { classes, columns, description, isFramed, rows, title } = props;

  const content = (
    <>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        {description}
      </Typography>
      <MaterialReactTable
        data={rows}
        columns={columns}
        getRowId={row => row.fieldKey}
        {...mappingTableProps}
      />
    </>
  );

  if (!isFramed) {
    return <Box className={classes.mappingWorkspace}>{content}</Box>;
  }

  return (
    <Box className={`${classes.sectionCard} ${classes.mappingWorkspace}`}>
      {content}
    </Box>
  );
};

ImportMappingTable.defaultProps = {
  isFramed: true,
};

ImportMappingTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  description: PropTypes.string.isRequired,
  isFramed: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
};

const VOUCHER_TEMPLATE = {
  templateId: 0,
  templateTypeId: 1,
  name: '',
  startingRow: 2,
  groupingColumn: null,
  useApprovalMode: false,
  mappings: [],
};

const VOUCHER_MAPPING = {
  voucherMappingId: 0,
  templateId: 0,
  fieldId: 0,
  fieldName: '',
  position: null,
  overrideValue: null,
};

const createVoucherMapping = row => ({
  ...VOUCHER_MAPPING,
  fieldId: row.fieldId,
  fieldName: row.fieldName,
});

const createVoucherTemplate = () => ({
  ...VOUCHER_TEMPLATE,
  mappings: VOUCHER_IMPORT_ROWS.map(createVoucherMapping),
});

const hydrateVoucherTemplate = template => {
  const freshTemplate = createVoucherTemplate();
  if (!template) return freshTemplate;

  const fetchedMappingsByFieldId = new Map(getTemplateMappings(template).map(mapping => [String(mapping.fieldId), mapping]));

  return {
    ...freshTemplate,
    ...template,
    templateId: template.templateId ?? freshTemplate.templateId,
    templateTypeId: template.templateTypeId ?? freshTemplate.templateTypeId,
    name: getTemplateName(template),
    startingRow: template.startingRow ?? freshTemplate.startingRow,
    groupingColumn: template.groupingColumn ?? freshTemplate.groupingColumn,
    useApprovalMode: template.useApprovalMode ?? freshTemplate.useApprovalMode,
    mappings: freshTemplate.mappings.map(mapping => {
      const fetchedMapping = fetchedMappingsByFieldId.get(String(mapping.fieldId));

      return fetchedMapping
        ? {
          ...mapping,
          ...fetchedMapping,
          fieldId: mapping.fieldId,
          fieldName: mapping.fieldName,
        }
        : mapping;
    }),
  };
};

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '';

  return String(value);
};

const getDisplayMappingRows = template => {
  const rowsByFieldId = new Map(VOUCHER_IMPORT_ROWS.map(row => [String(row.fieldId), row]));
  const mappings = getTemplateMappings(template);
  const mappingsByFieldId = new Map(mappings.map(mapping => [String(mapping.fieldId), mapping]));

  const knownRows = VOUCHER_IMPORT_ROWS.reduce((displayRows, row) => {
    const mapping = mappingsByFieldId.get(String(row.fieldId));
    if (!mapping) return displayRows;

    return [
      ...displayRows,
      {
        ...row,
        ...mapping,
        fieldKey: row.fieldKey,
        fieldName: row.fieldName,
        dataLevel: row.dataLevel,
        dataType: row.dataType,
        overrideValue: mapping.position !== null && mapping.position !== undefined ? null : mapping.overrideValue,
      },
    ];
  }, []);

  const unknownRows = mappings.filter(mapping => !rowsByFieldId.has(String(mapping.fieldId))).map(mapping => {
    const row = rowsByFieldId.get(String(mapping.fieldId));

    return {
      ...row,
      ...mapping,
      fieldKey: row?.fieldKey ?? `field-${mapping.fieldId}`,
      fieldName: row?.fieldName ?? mapping.fieldName,
      dataLevel: row?.dataLevel ?? '',
      dataType: row?.dataType ?? '',
      overrideValue: mapping.position !== null && mapping.position !== undefined ? null : mapping.overrideValue,
    };
  });

  return [...knownRows, ...unknownRows];
};

const voucherImportTemplateDisplayColumns = [
  { accessorKey: 'fieldName', header: 'Field Name' },
  {
    accessorKey: 'position',
    header: 'Position',
    Cell: ({ cell }) => getDisplayValue(cell.getValue()),
  },
  {
    accessorKey: 'overrideValue',
    header: 'Override Value',
    Cell: ({ cell }) => getDisplayValue(cell.getValue()),
  },
  {
    accessorKey: 'dataType',
    header: 'Data Type',
    Cell: ({ row }) => getDataTypeLabel(row.original),
  },
  { accessorKey: 'dataLevel', header: 'Data Level' },
];

const getImportResultErrors = importResults => importResults?.errors ?? importResults?.data?.errors ?? [];
const getImportResultVoucherDetails = importResults => importResults?.voucherDetails ?? importResults?.data?.voucherDetails ?? [];
const getImportResultTransferees = importResults => importResults?.transferees ?? importResults?.data?.transferees ?? [];
const getImportResultVendors = importResults => importResults?.vendors ?? importResults?.data?.vendors ?? [];
const getImportResultVoucherLineItemAuthorizationLinks = importResults => importResults?.voucherLineItemAuthorizationLinks ?? importResults?.data?.voucherLineItemAuthorizationLinks ?? [];
const getCurrencyValue = value => currencyFormatter({ value: Number(value || 0) });
const getLineItemTotal = lineItems => lineItems.reduce((total, lineItem) => total + Number(lineItem.totalAmount || 0), 0);
const isSameId = (firstValue, secondValue) => Boolean(firstValue && secondValue) && String(firstValue).toLowerCase() === String(secondValue).toLowerCase();

const getLookupTitle = (items, id) => {
  if (!id) return '';

  const item = items?.find(value => String(value.id) === String(id) || String(value.value) === String(id));
  return item?.title ?? item?.name ?? String(id);
};

const getVoucherDetailId = voucherDetail => voucherDetail?.voucherId ?? voucherDetail?.id ?? voucherDetail?.voucherInformation?.voucherId ?? voucherDetail?.voucherInformation?.id ?? null;


const getPayeeLabel = (voucherDetail, importResults, enumerations) => {
  const { payee = {} } = voucherDetail;

  if (isSameId(payee.payeeTypeId, GUIDS.PAYEE_TYPE.VENDOR)) {
    const vendor = getImportResultVendors(importResults).find(value => String(value.vendorId) === String(payee.vendorId));
    return vendor?.companyName ?? vendor?.vendorCode ?? `Vendor ${payee.vendorId}`;
  }

  if (isSameId(payee.payeeTypeId, GUIDS.PAYEE_TYPE.TRANSFEREE)) {
    const transferee = getImportResultTransferees(importResults).find(value => String(value.fileId) === String(payee.authorizationId));

    return transferee?.fileNumber ? `File ${transferee.fileNumber}` : getLookupTitle(enumerations?.payeeTypes, payee.payeeTypeId);
  }

  return getLookupTitle(enumerations?.payeeTypes, payee.payeeTypeId);
};

const getImportResultVoucherRows = (importResults, enumerations) => getImportResultVoucherDetails(importResults).map((voucherDetail, index) => {
  const voucherInformation = voucherDetail.voucherInformation ?? {};
  const lineItems = voucherDetail.voucherLineItems ?? [];

  return {
    id: `voucher-${index}`,
    batchType: getLookupTitle(enumerations?.batchTypes, voucherInformation.batchTypeId),
    detail: voucherDetail,
    index,
    lineItemCount: lineItems.length,
    payee: getPayeeLabel(voucherDetail, importResults, enumerations),
    totalAmount: getLineItemTotal(lineItems),
    voucherId: getVoucherDetailId(voucherDetail),
    voucherType: getLookupTitle(enumerations?.voucherTypes, voucherInformation.voucherTypeId),
  };
});

const getImportResultLineItemRows = (voucherDetail, importResults, voucherIndex) => (voucherDetail.voucherLineItems ?? []).map((lineItem, index) => {
  const authorizationLink = getImportResultVoucherLineItemAuthorizationLinks(importResults).find(link => link.voucherIndex === voucherIndex && link.lineItemIndex === index);

  return {
    id: `line-item-${index}`,
    amount: lineItem.totalAmount,
    description: lineItem.description ?? '',
    fileNumber: authorizationLink?.fileNumber ?? '',
  };
});

const getVoucherImportResultColumns = showVoucherLinks => [
  ...showVoucherLinks ? [{
    accessorKey: 'view',
    header: 'View',
    maxSize: 56,
    minSize: 56,
    size: 56,
    muiTableBodyCellProps: {
      align: 'center',
      sx: {
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        px: 0.5,
      },
    },
    muiTableHeadCellProps: {
      align: 'center',
      sx: {
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        px: 0.5,
      },
    },
    Cell: ({ row }) => {
      const { voucherId } = row.original;
      if (!voucherId) return null;

      return (
        <Tooltip title="View voucher">
          <IconButton
            aria-label={`View voucher ${voucherId}`}
            component="a"
            href={`/vouchers/${voucherId}`}
            rel="noopener noreferrer"
            size="small"
            target="_blank"
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },
  }] : [],
  { accessorKey: 'voucherType', header: 'Voucher Type' },
  { accessorKey: 'batchType', header: 'Batch Type' },
  { accessorKey: 'payee', header: 'Paid To' },
  {
    accessorKey: 'totalAmount',
    header: 'Total Amount',
    Cell: ({ cell }) => getCurrencyValue(cell.getValue()),
  },
  { accessorKey: 'lineItemCount', header: 'Line Items' },
];

const VoucherImportResultLineItems = props => {
  const { lineItems } = props;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
      <Grid container sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', fontWeight: 600, px: 1.5, py: 1 }}>
        <Grid item xs={3}>File Number</Grid>
        <Grid item xs={3}>Amount</Grid>
        <Grid item xs={6}>Description</Grid>
      </Grid>
      {lineItems.map(lineItem => (
        <Grid key={lineItem.id} container sx={{ borderBottom: '1px solid #eeeeee', px: 1.5, py: 1, '&:last-child': { borderBottom: 0 } }}>
          <Grid item xs={3}>{lineItem.fileNumber}</Grid>
          <Grid item xs={3}>{getCurrencyValue(lineItem.amount)}</Grid>
          <Grid item xs={6}>{lineItem.description}</Grid>
        </Grid>
      ))}
    </Box>
  );
};

VoucherImportResultLineItems.propTypes = {
  lineItems: PropTypes.arrayOf(PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    fileNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    id: PropTypes.string.isRequired,
  })).isRequired,
};

const VoucherImportResults = props => {
  const { emptyMessage, enumerations, errorTitle, importResults, showVoucherLinks, successMessage } = props;

  if (!importResults) {
    return (
      <Box pt={2}>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const errors = getImportResultErrors(importResults);
  if (errors.length > 0) {
    return (
      <Box pt={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorTitle} {errors.length} issue{errors.length === 1 ? '' : 's'}.
        </Alert>
        <List dense>
          {errors.map((error, index) => (
            <ListItem key={`${error}-${index}`}>
              <Typography variant="body2">{error}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  const voucherRows = getImportResultVoucherRows(importResults, enumerations);
  const totalLineItems = voucherRows.reduce((total, voucherRow) => total + voucherRow.lineItemCount, 0);
  const totalAmount = voucherRows.reduce((total, voucherRow) => total + voucherRow.totalAmount, 0);

  return (
    <Box pt={2}>
      <Typography variant="body2" color="textSecondary" mb={2}>
        {successMessage}
      </Typography>
      <Stack direction="row" spacing={4} mb={2}>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Vouchers</Typography>
          <Typography variant="h6">{voucherRows.length}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Line Items</Typography>
          <Typography variant="h6">{totalLineItems}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Amount</Typography>
          <Typography variant="h6">{getCurrencyValue(totalAmount)}</Typography>
        </Box>
      </Stack>
      <MaterialReactTable
        data={voucherRows}
        columns={getVoucherImportResultColumns(showVoucherLinks)}
        getRowId={row => row.id}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        renderDetailPanel={({ row }) => (
          <Box sx={{ width: '100%' }}>
            <VoucherImportResultLineItems lineItems={getImportResultLineItemRows(row.original.detail, importResults, row.original.index)} />
          </Box>
        )}
      />
    </Box>
  );
};

VoucherImportResults.propTypes = {
  emptyMessage: PropTypes.string.isRequired,
  enumerations: PropTypes.object,
  errorTitle: PropTypes.string.isRequired,
  importResults: PropTypes.object,
  showVoucherLinks: PropTypes.bool,
  successMessage: PropTypes.string.isRequired,
};

VoucherImportResults.defaultProps = {
  enumerations: null,
  importResults: null,
  showVoucherLinks: false,
};

const TabPanel = props => {
  const { children, currentTab, value } = props;

  if (currentTab !== value) return null;

  return <Box pt={2}>{children}</Box>;
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  currentTab: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const SelectedVoucherImportTemplate = props => {
  const {
    activeTab,
    classes,
    dryRunResults,
    enumerations,
    importResults,
    isDryRunLoading,
    isImportSubmitting,
    isLoading,
    onDryRun,
    onFileSelected,
    onSubmitImport,
    onTabChange,
    selectedTemplateFile,
    template,
    canImport = false,
  } = props;
  const hasDryRunErrors = getImportResultErrors(dryRunResults).length > 0;
  const canRunDryRun = Boolean(canImport && selectedTemplateFile && !isDryRunLoading && !isImportSubmitting);
  const canSubmitImport = Boolean(
    canImport && selectedTemplateFile && dryRunResults && !hasDryRunErrors && !isDryRunLoading && !isImportSubmitting
  );

  if (isLoading) {
    return (
      <Box className={classes.sectionCard}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Spinner size="1x" />
          <Typography variant="body2">Loading voucher import template...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!template) return null;

  return (
    <>
      <Box className={classes.sectionCard}>
        <Grid container rowSpacing={1} columnSpacing={3}>
          <Grid item xs={3}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
              <UploadTemplateFile onFileSelected={onFileSelected} />
              <MuiButton
                variant="contained"
                color="primary"
                disabled={!canRunDryRun}
                onClick={onDryRun}
                startIcon={isDryRunLoading ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isDryRunLoading ? 'Running...' : 'Dry Run'}
              </MuiButton>
              <MuiButton
                variant="contained"
                color="primary"
                disabled={!canSubmitImport}
                onClick={onSubmitImport}
                startIcon={isImportSubmitting ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isImportSubmitting ? 'Submitting...' : 'Submit'}
              </MuiButton>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="textSecondary">Template Name</Typography>
            <Typography variant="body1">{getTemplateName(template)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="textSecondary">Data Starting Row</Typography>
            <Typography variant="body1">{getDisplayValue(template.startingRow)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="textSecondary">Grouping Column</Typography>
            <Typography variant="body1">{getDisplayValue(template.groupingColumn)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="textSecondary">Create vouchers as approved</Typography>
            <Typography variant="body1">{template.useApprovalMode ? 'Yes' : 'No'}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.sectionCard}>
        <Tabs value={activeTab} onChange={(event, nextTab) => onTabChange(nextTab)} indicatorColor="primary" textColor="primary">
          <Tab value={VOUCHER_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS} label="Mapping Definitions" sx={{ px: 4 }} />
          <Tab value={VOUCHER_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS} label="Dry Run Results" disabled={!dryRunResults} sx={{ px: 4 }} />
          <Tab value={VOUCHER_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS} label="Import Results" disabled={!importResults} sx={{ px: 4 }} />
        </Tabs>
        <TabPanel currentTab={activeTab} value={VOUCHER_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS}>
          <ImportMappingTable
            classes={classes}
            title="Voucher Field Mappings"
            description="Template mapping values for the selected voucher import template."
            rows={getDisplayMappingRows(template)}
            columns={voucherImportTemplateDisplayColumns}
            isFramed={false}
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={VOUCHER_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS}>
          <VoucherImportResults
            emptyMessage="Upload a file and run a dry run to preview import results."
            enumerations={enumerations}
            errorTitle="Dry run found"
            importResults={dryRunResults}
            successMessage="These vouchers and line items will be created when the import is submitted."
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={VOUCHER_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS}>
          <VoucherImportResults
            emptyMessage="Submit a successfully validated file to view import results."
            enumerations={enumerations}
            errorTitle="Import found"
            importResults={importResults}
            showVoucherLinks
            successMessage="These vouchers and line items were returned by the import submission."
          />
        </TabPanel>
      </Box>
    </>
  );
};

SelectedVoucherImportTemplate.propTypes = {
  activeTab: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  dryRunResults: PropTypes.object,
  enumerations: PropTypes.object,
  importResults: PropTypes.object,
  isDryRunLoading: PropTypes.bool.isRequired,
  isImportSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDryRun: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  onSubmitImport: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedTemplateFile: PropTypes.object,
  template: PropTypes.object,
};

SelectedVoucherImportTemplate.defaultProps = {
  dryRunResults: null,
  enumerations: null,
  importResults: null,
  selectedTemplateFile: null,
  template: null,
};

const hasMappingValue = value => value !== null && value !== undefined;

const getPayloadOverrideValue = value => (value === null || value === undefined ? null : String(value));

const REQUIRED_MAPPING_ERROR = 'Field is required with no position or override value specified';
const REQUIRED_TEMPLATE_NAME_ERROR = 'Template name is required';
const DATA_STARTING_ROW_ERROR = 'Data starting row is required and must be greater than zero';
const GROUPING_COLUMN_ERROR = 'Grouping column is required and must be greater than zero';

const buildVoucherTemplatePayload = template => {
  const mappingsByFieldId = new Map(template.mappings.map(mapping => [mapping.fieldId, mapping]));

  return {
    ...template,
    mappings: VOUCHER_IMPORT_ROWS.reduce((mappings, row) => {
      const mapping = mappingsByFieldId.get(row.fieldId) || createVoucherMapping(row);

      if (mapping.position !== null) {
        return [
          ...mappings,
          {
            ...mapping,
            overrideValue: null,
          },
        ];
      }

      if (hasMappingValue(mapping.overrideValue)) {
        return [
          ...mappings,
          {
            ...mapping,
            overrideValue: getPayloadOverrideValue(mapping.overrideValue),
          },
        ];
      }

      if (hasMappingValue(row.defaultValue)) {
        return [
          ...mappings,
          {
            ...mapping,
            overrideValue: getPayloadOverrideValue(row.defaultValue),
          },
        ];
      }

      return row.required ? [...mappings, { ...mapping }] : mappings;
    }, []),
  };
};

const getRequiredMappingErrors = template => {
  const mappingsByFieldId = new Map(template.mappings.map(mapping => [mapping.fieldId, mapping]));

  return VOUCHER_IMPORT_ROWS.reduce((errors, row) => {
    const mapping = mappingsByFieldId.get(row.fieldId) || createVoucherMapping(row);

    if (
      row.required
      && mapping.position === null
      && !hasMappingValue(mapping.overrideValue)
      && !hasMappingValue(row.defaultValue)
    ) {
      return {
        ...errors,
        [row.fieldId]: REQUIRED_MAPPING_ERROR,
      };
    }

    return errors;
  }, {});
};

const getPositionValidationErrors = errors => Object.entries(errors).reduce((positionValidationErrors, [fieldId, error]) => (
  error === REQUIRED_MAPPING_ERROR
    ? positionValidationErrors
    : {
      ...positionValidationErrors,
      [fieldId]: error,
    }
), {});

const hasErrors = errors => Object.keys(errors).length > 0;

const FORM_ERROR_MESSAGE = 'Please fix the highlighted errors before saving.';

const getPositiveNumberError = (value, message) => {
  if (value === null || value === undefined || value === '') return message;

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? null : message;
};

const getPositiveNumberValue = value => {
  if (value === '') return '';

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : value;
};

const getTemplateFieldError = (field, value) => {
  switch (field) {
    case 'name':
      return value.trim() ? null : REQUIRED_TEMPLATE_NAME_ERROR;
    case 'startingRow':
      return getPositiveNumberError(value, DATA_STARTING_ROW_ERROR);
    case 'groupingColumn':
      return value === null ? null : getPositiveNumberError(value, GROUPING_COLUMN_ERROR);
    default:
      return null;
  }
};

const getTemplateErrors = template => ['name', 'startingRow', 'groupingColumn'].reduce((errors, field) => {
  const error = getTemplateFieldError(field, template[field]);

  return error
    ? {
      ...errors,
      [field]: error,
    }
    : errors;
}, {});

const getErrorAdornmentProps = error => ({
  endAdornment: error ? (
    <InputAdornment position="end">
      <Tooltip title={error}>
        <ErrorOutline color="error" fontSize="small" />
      </Tooltip>
    </InputAdornment>
  ) : null,
});

const getPositionError = value => {
  if (value === '') return null;

  const position = Number(value);
  return Number.isFinite(position) && position > 0
    ? null
    : 'Position must be a number greater than zero';
};

const getPositionValue = value => {
  if (value === '') return null;

  const position = Number(value);
  return Number.isFinite(position) && position > 0 ? position : value;
};

const VoucherFileTemplateEditor = props => {
  const {
    classes,
    enumerations,
    initialTemplate,
    isEditMode,
    onClose,
    onSaveSuccess,
    open,
    postVoucherImportTemplate: createVoucherImportTemplate,
    putVoucherImportTemplate: updateVoucherImportTemplate,
    showToast: showToastMessage,
  } = props;
  const [template, setTemplate] = useState(createVoucherTemplate);
  const [isSaving, setIsSaving] = useState(false);
  const [templateErrors, setTemplateErrors] = useState({});
  const [positionErrors, setPositionErrors] = useState({});

  const enumerationTitles = useMemo(() => getEnumerationTitles(enumerations), [enumerations]);
  const groupLineItemsChecked = template.groupingColumn !== null;

  const mappingRows = useMemo(() => {
    const mappingsByFieldId = new Map(template.mappings.map(mapping => [mapping.fieldId, mapping]));

    return VOUCHER_IMPORT_ROWS.map(row => {
      const mapping = mappingsByFieldId.get(row.fieldId) || createVoucherMapping(row);

      return {
        ...row,
        position: mapping.position,
        overrideValue: mapping.position !== null ? null : mapping.overrideValue,
        positionError: positionErrors[row.fieldId] || null,
      };
    });
  }, [positionErrors, template.mappings]);

  useEffect(() => {
    if (!open) return;

    setTemplate(isEditMode ? hydrateVoucherTemplate(initialTemplate) : createVoucherTemplate());
    setTemplateErrors({});
    setPositionErrors({});
  }, [initialTemplate, isEditMode, open]);

  const updateTemplateValue = (field, value) => {
    setTemplate(currentTemplate => ({
      ...currentTemplate,
      [field]: value,
    }));

    setTemplateErrors(currentErrors => {
      if (!currentErrors[field]) return currentErrors;

      const error = getTemplateFieldError(field, value);
      const nextErrors = { ...currentErrors };

      if (error) {
        nextErrors[field] = error;
      } else {
        delete nextErrors[field];
      }

      return nextErrors;
    });
  };

  const updatePositionError = (fieldId, value) => {
    const error = getPositionError(value);

    setPositionErrors(currentErrors => {
      const nextErrors = { ...currentErrors };

      if (error) {
        nextErrors[fieldId] = error;
      } else {
        delete nextErrors[fieldId];
      }

      return nextErrors;
    });
  };

  const updatePosition = (fieldId, value) => {
    updatePositionError(fieldId, value);

    const position = getPositionValue(value);

    setTemplate(currentTemplate => ({
      ...currentTemplate,
      mappings: currentTemplate.mappings.map(mapping => (
        mapping.fieldId === fieldId
          ? {
            ...mapping,
            position,
            overrideValue: position !== null ? null : mapping.overrideValue,
          }
          : mapping
      )),
    }));
  };

  const updateOverrideValue = (fieldKey, value) => {
    const row = VOUCHER_IMPORT_ROWS.find(importRow => importRow.fieldKey === fieldKey);
    if (!row) return;

    setTemplate(currentTemplate => ({
      ...currentTemplate,
      mappings: currentTemplate.mappings.map(mapping => (
        mapping.fieldId === row.fieldId
          ? {
            ...mapping,
            position: value !== null ? null : mapping.position,
            overrideValue: value,
          }
          : mapping
      )),
    }));

    setPositionErrors(currentErrors => {
      if (!currentErrors[row.fieldId] || value === null) return currentErrors;

      const nextErrors = { ...currentErrors };
      delete nextErrors[row.fieldId];

      return nextErrors;
    });
  };

  const resetEditor = () => {
    setTemplate(createVoucherTemplate());
    setTemplateErrors({});
    setPositionErrors({});
  };

  const handleClose = (...args) => {
    resetEditor();
    onClose(...args);
  };

  const handleSave = async () => {
    if (isSaving) return;

    const nextTemplateErrors = getTemplateErrors(template);
    const requiredMappingErrors = getRequiredMappingErrors(template);
    const nextPositionErrors = {
      ...getPositionValidationErrors(positionErrors),
      ...requiredMappingErrors,
    };

    setTemplateErrors(nextTemplateErrors);
    setPositionErrors(nextPositionErrors);

    if (hasErrors(nextTemplateErrors) || hasErrors(nextPositionErrors)) return;

    const payload = buildVoucherTemplatePayload(template);
    const templateId = getTemplateId(payload);

    if (isEditMode && !templateId) {
      showToastMessage('Unable to update voucher import template because the template ID was not found.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setIsSaving(true);

    const response = isEditMode
      ? await updateVoucherImportTemplate(templateId, payload)
      : await createVoucherImportTemplate(payload);
    const failureType = isEditMode ? PUT_VOUCHER_IMPORT_TEMPLATE_FAILURE : POST_VOUCHER_IMPORT_TEMPLATE_FAILURE;

    setIsSaving(false);

    if (response.type === failureType) {
      showToastMessage('Failed to save voucher import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Voucher import template saved successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    onSaveSuccess(response.response);
    handleClose();
  };

  const formErrorMessage = hasErrors(templateErrors) || hasErrors(positionErrors) ? FORM_ERROR_MESSAGE : '';

  const mappingColumns = getVoucherImportColumnMappings({
    enumerationTitles,
    onOverrideValueChange: updateOverrideValue,
    onPositionChange: updatePosition,
  });

  return (
    <ImportTemplateEditor
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Edit Voucher File Import Template' : 'Create Voucher File Import Template'}
      secondaryText="Define the template details and field mappings for a voucher file import"
      actions={(
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          {formErrorMessage && (
            <Typography color="error" variant="body2">
              {formErrorMessage}
            </Typography>
          )}
          <MuiButton onClick={handleClose} disabled={isSaving}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <Spinner size="1x" color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Template' : 'Save Template'}
          </MuiButton>
        </Stack>
      )}
    >
      <Box className={classes.sectionCard}>
        <Grid container rowSpacing={1} columnSpacing={3}>
          <Grid item xs={4}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch checked={template.useApprovalMode} onChange={event => updateTemplateValue('useApprovalMode', event.target.checked)} />}
              label="Create vouchers as approved"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch checked={groupLineItemsChecked} onChange={event => updateTemplateValue('groupingColumn', event.target.checked ? '' : null)} />}
              label="Group line items"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Template Name"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.name)}
              value={template.name}
              InputProps={getErrorAdornmentProps(templateErrors.name)}
              onChange={event => updateTemplateValue('name', event.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Data Starting Row"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.startingRow)}
              value={template.startingRow}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              InputProps={getErrorAdornmentProps(templateErrors.startingRow)}
              onChange={event => updateTemplateValue('startingRow', getPositiveNumberValue(event.target.value))}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Grouping Column"
              variant="outlined"
              size="small"
              disabled={!groupLineItemsChecked}
              required={groupLineItemsChecked}
              error={Boolean(templateErrors.groupingColumn)}
              value={template.groupingColumn || ''}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              InputProps={getErrorAdornmentProps(templateErrors.groupingColumn)}
              onChange={event => updateTemplateValue('groupingColumn', getPositiveNumberValue(event.target.value))} />
          </Grid>
        </Grid>
      </Box>

      <ImportMappingTable
        classes={classes}
        title="Voucher Field Mappings"
        description="Identify the numerical column position of each field in the voucher file to be imported. Fields marked with an asterisk (*) are required. If a field is not present in the file, you may specify an override value to be used for all vouchers created from the import."
        rows={mappingRows}
        columns={mappingColumns}
      />
    </ImportTemplateEditor>
  );
};

VoucherFileTemplateEditor.propTypes = {
  classes: PropTypes.object.isRequired,
  enumerations: PropTypes.object,
  initialTemplate: PropTypes.object,
  isEditMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  postVoucherImportTemplate: PropTypes.func.isRequired,
  putVoucherImportTemplate: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
};

VoucherFileTemplateEditor.defaultProps = {
  enumerations: null,
  initialTemplate: null,
};


function ImportVoucherFilesView(props) {
  const {
    classes,
    enumerations,
    getVoucherImportTemplate: loadVoucherImportTemplate,
    getVoucherImportTemplates: loadVoucherImportTemplateList,
    getAllCurrencies: loadAllCurrencies,
    getAllEnumerations: loadAllEnumerations,
    setPageTitle: setImportPageTitle,
    showToast: showToastMessage,
    postVoucherImportTemplate: saveVoucherImportTemplate,
    putVoucherImportTemplate: updateVoucherImportTemplate,
    submitVoucherImportTemplateFile: submitVoucherImportTemplateFileForImport,
    uploadVoucherImportTemplateFile: dryRunVoucherImportTemplateFile,
  } = props;

  const [voucherImportTemplates, setVoucherImportTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isDryRunLoading, setIsDryRunLoading] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [selectedTemplateTab, setSelectedTemplateTab] = useState(VOUCHER_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);

  const fetchVoucherImportTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);

    const response = await loadVoucherImportTemplateList();

    setIsLoadingTemplates(false);

    if (response.type === GET_VOUCHER_IMPORT_TEMPLATES_FAILURE) {
      showToastMessage('Failed to load voucher import templates. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setVoucherImportTemplates(getTemplateList(response.response));
  }, [loadVoucherImportTemplateList, showToastMessage]);

  const handleTemplateSelected = useCallback(async templateId => {
    setSelectedTemplateId(templateId);
    setSelectedTemplate(null);
    setEditorTemplate(null);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateFile(null);
    setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);

    if (!templateId) return;

    setIsLoadingTemplate(true);

    const response = await loadVoucherImportTemplate(templateId);

    setIsLoadingTemplate(false);

    if (response.type === GET_VOUCHER_IMPORT_TEMPLATE_FAILURE) {
      showToastMessage('Failed to load voucher import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setSelectedTemplate(response.response);
  }, [loadVoucherImportTemplate, showToastMessage]);

  const openTemplateEditor = () => {
    setEditorTemplate(selectedTemplate || null);
    setTemplateEditorOpen(true);
  };

  const handleFileSelected = file => {
    setSelectedTemplateFile(file);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  };

  const handleDryRun = async () => {
    const templateId = selectedTemplateId || getTemplateId(selectedTemplate);

    if (!templateId || !selectedTemplateFile || isDryRunLoading || isImportSubmitting) return;

    setIsDryRunLoading(true);
    setImportResults(null);

    const response = await dryRunVoucherImportTemplateFile(templateId, selectedTemplateFile);

    setIsDryRunLoading(false);

    if (response.type === POST_VOUCHER_IMPORT_TEMPLATE_DRYRUN_FAILURE) {
      setDryRunResults({ errors: response.messages ?? ['Failed to upload voucher import file. Please try again.'] });
      setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);
      showToastMessage('Failed to upload voucher import file. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setDryRunResults(response.response);
    setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);

    if (getImportResultErrors(response.response).length > 0) {
      showToastMessage('Voucher import dry run completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Voucher import dry run completed successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  const handleSubmitImport = async () => {
    const templateId = selectedTemplateId || getTemplateId(selectedTemplate);

    if (
      !templateId
      || !selectedTemplateFile
      || !dryRunResults
      || getImportResultErrors(dryRunResults).length > 0
      || isDryRunLoading
      || isImportSubmitting
    ) return;

    setIsImportSubmitting(true);

    const response = await submitVoucherImportTemplateFileForImport(templateId, selectedTemplateFile);

    setIsImportSubmitting(false);

    if (response.type === POST_VOUCHER_IMPORT_TEMPLATE_SUBMIT_FAILURE) {
      setImportResults({ errors: response.messages ?? ['Failed to submit voucher import file. Please try again.'] });
      setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);
      showToastMessage('Failed to submit voucher import file. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setImportResults(response.response);
    setSelectedTemplateTab(VOUCHER_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);

    if (getImportResultErrors(response.response).length > 0) {
      showToastMessage('Voucher import completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Voucher import submitted successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  const handleTemplateSaveSuccess = async savedTemplate => {
    await fetchVoucherImportTemplates();

    const savedTemplateId = getTemplateId(savedTemplate);
    if (savedTemplateId) {
      await handleTemplateSelected(String(savedTemplateId));
      return;
    }

    if (selectedTemplateId) {
      await handleTemplateSelected(selectedTemplateId);
    }
  };

  useEffect(() => {
    setImportPageTitle('Import Voucher Files');
  }, [setImportPageTitle]);

  useEffect(() => {
    fetchVoucherImportTemplates();
  }, [fetchVoucherImportTemplates]);

  useEffect(() => {
    if (!enumerations) {
      loadAllEnumerations();
    }
  }, [enumerations, loadAllEnumerations]);

  useEffect(() => {
    if (enumerations && !enumerations.currencies) {
      loadAllCurrencies();
    }
  }, [enumerations, loadAllCurrencies]);

  const canImportVouchers = hasPermission(Permissions.Finance.Vouchers.Import);
  const canEditVouchers = hasPermission(Permissions.Finance.Vouchers.Edit);

  if (!canImportVouchers) {
    props.history.push('/');
    return null;
  }

  return (
    <div className={`${classes.root} p-2`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <VoucherFileTemplateSelector
                  isLoading={isLoadingTemplates}
                  templates={voucherImportTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onTemplateSelected={handleTemplateSelected}
                />
              </Stack>
              <VoucherImportTemplateEditorButton
                isEditMode={Boolean(selectedTemplate)}
                disabled={isLoadingTemplate || !canEditVouchers}
                onClick={openTemplateEditor}
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {(selectedTemplateId || isLoadingTemplate) && (
            <SelectedVoucherImportTemplate
              activeTab={selectedTemplateTab}
              classes={classes}
              dryRunResults={dryRunResults}
              enumerations={enumerations}
              importResults={importResults}
              isDryRunLoading={isDryRunLoading}
              isImportSubmitting={isImportSubmitting}
              isLoading={isLoadingTemplate}
              onDryRun={handleDryRun}
              onFileSelected={handleFileSelected}
              onSubmitImport={handleSubmitImport}
              onTabChange={setSelectedTemplateTab}
              selectedTemplateFile={selectedTemplateFile}
              template={selectedTemplate}
              canImport={canImportVouchers}
            />
          )}
        </Grid>
      </Grid>
      <VoucherFileTemplateEditor
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        classes={classes}
        enumerations={enumerations}
        initialTemplate={editorTemplate}
        isEditMode={Boolean(editorTemplate)}
        onSaveSuccess={handleTemplateSaveSuccess}
        postVoucherImportTemplate={saveVoucherImportTemplate}
        putVoucherImportTemplate={updateVoucherImportTemplate}
        showToast={showToastMessage}
      />
    </div>
  );
}

ImportVoucherFilesView.propTypes = {
  classes: PropTypes.object.isRequired,
  enumerations: PropTypes.object,
  getVoucherImportTemplate: PropTypes.func.isRequired,
  getVoucherImportTemplates: PropTypes.func.isRequired,
  getAllCurrencies: PropTypes.func.isRequired,
  getAllEnumerations: PropTypes.func.isRequired,
  postVoucherImportTemplate: PropTypes.func.isRequired,
  putVoucherImportTemplate: PropTypes.func.isRequired,
  setPageTitle: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  submitVoucherImportTemplateFile: PropTypes.func.isRequired,
  uploadVoucherImportTemplateFile: PropTypes.func.isRequired,
};

ImportVoucherFilesView.defaultProps = {
  enumerations: null,
};

const mapStateToProps = state => ({
  enumerations: state.enumerations ? state.enumerations.allEnums : null,
});

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    getVoucherImportTemplate,
    getVoucherImportTemplates,
    getAllCurrencies,
    getAllEnumerations,
    postVoucherImportTemplate,
    putVoucherImportTemplate,
    setPageTitle,
    showToast,
    submitVoucherImportTemplateFile,
    uploadVoucherImportTemplateFile,
  }),
)(ImportVoucherFilesView);
